import React, { useState, useRef, useEffect } from 'react';
import MoodForm from '../components/MoodForm';
import TrackList from '../components/TrackList';
import { postMood, getMoreSongs, deleteTrack } from '../api/moodApi';

const groupedMoodSuggestions = [
  {
    label: "🌙 Chill & Everyday",
    prompts: [
      "I am chilling in my bedroom right now. Hit me with some r&b please.",
      "I am working on my laptop right now. Give me some lo-fi beats.",
      "I am watching a movie right now. Give me some movie soundtracks.",
      "I am reading a book right now.",
      "It’s raining outside and I just made some tea.",
      "Need lo-fi beats for working quietly.",
      "Soft sounds for writing late at night.",
      "Give me chill music for sunset.",
      "Cooking dinner and sipping wine.",
      "Songs for a sunny afternoon walk.",
      "Good songs for a late-night drive?",
      "Woke up feeling blissfully calm.",
      "City lights outside my window, show me soulful late-night synths.",
      "Can't sleep — need calm piano without vocals to clear my head.",
      "Morning sunlight and fresh coffee — give me upbeat acoustic songs.",
      "Packing for a solo trip and need adventurous indie tunes."
    ]
  },
  {
    label: "❤️ Love & Crush",
    prompts: [
      "In a Korean drama right now. Hit me with some Korean R&B please.",
      "I just met a Korean girl and I think I have a crush on her.",
      "Just texted my crush and I can’t stop smiling. Play me something flirty.",
      "I went on a first date today and it feels like butterflies. What do I listen to?",
      "Thinking about someone special — I want soulful ballads.",
      "Feeling like dancing around alone.",
      "Need dreamy Ariana Grande vibes for late-night texting.",
      "Feeling heartbroken — choose Adele songs that help me heal.",
      "Give me NewJeans tracks that feel like blush-worthy crush anthems."
    ]
  },
  {
    label: "🌧 Sad & Reflective",
    prompts: [
      "Just got dumped. Need something emotional.",
      "Longing for home. Play something warm.",
      "Got the blues. I need something moody.",
      "I am feeling sad right now. Hit me with some sad songs please.",
      "Feeling lonely tonight, just me and my thoughts.",
      "Missing someone far away. Hit me with some nostalgic R&B.",
      "Rain on the window while I journal — give me gentle string instrumentals.",
      "Need piano soundscapes for a quiet cry.",
      "Feeling empty after a long week — surprise me with reflective jazz."
    ]
  },
  {
    label: "⚡ Hype & Energy",
    prompts: [
      "Hyped for the gym — give me energy.",
      "Just crushed my workout — need high-energy tracks.",
      "Heading out with friends, mood is lit. Give me party jams.",
      "About to play some tennis, pump me up with fast tracks.",
      "Feeling rebellious and fired up.",
      "I need festival-ready EDM to wake me up.",
      "Pre-game with high-energy K-pop bangers.",
      "Give me Latin trap that makes me feel unstoppable."
    ]
  },
  {
    label: "🌸 Aesthetic & Special Moments",
    prompts: [
      "I’m feeling nostalgic about my childhood summers.",
      "Golden hour on my balcony, everything feels magical.",
      "Walking through Berkeley campus at sunset. Hit me with indie vibes.",
      "Looking at the stars, thinking about life. What’s a good soundtrack for this?",
      "Songs that sound like ocean waves.",
      "Craving ambient soundscapes to relax.",
      "What would match a fantasy novel mood?",
      "Drawing in my sketchbook — I want Studio Ghibli forest vibes.",
      "Need songs that feel like walking through a museum at night.",
      "Give me dreamy shoegaze for a foggy morning drive along the coast."
    ]
  },
  {
    label: "🎤 Artist / Band Specific",
    prompts: [
      "Hit me with some songs by the Kpop group NewJeans.",
      "I want to listen to some IU’s songs.",
      "I just went to Ed Sheeran’s concert, I want more of his songs!",
      "I’ve been hearing the Kpop band BTS a lot. Let me try some of their music.",
      "Play me some of Drake’s smooth tracks.",
      "Give me some of Beyoncé’s best ballads.",
      "I’m in the mood for classical — play me some Chopin or Rachmaninoff.",
      "Only Billie Eilish tracks that feel like slow motion.",
      "Give me The Weeknd songs for night driving.",
      "I crave soulful Kehlani tracks for self-care time.",
      "Feeling reflective — play Taylor Swift's folklore-era gems.",
      "Need calm Lofi Girl mixes featuring Japanese producers.",
      "Give me SZA songs for a late-night wind-down.",
      "Play me some Frank Ocean's songs that feel like hazy summer evenings.",
      "Need Fleetwood Mac deep cuts for a sunset road trip.",
      "Only Mitski songs that keep me calm but focused.",
      "Queue Rosalia flamenco-pop for a confident solo walk.",
      "Give me Stray Kids high-energy tracks for the gym.",
      "I want Post Malone tunes for a chill house party.",
      "Play me some Phoebe Bridgers's songs that help me process feelings.",
      "Serve me Daft Punk grooves for a neon city drive.",
      "I need Queen's epic anthems before a big presentation.",
      "Give me Justin Bieber soft songs for a cozy evening.",
      "Play me some Shawn Mendes's tracks that feel like hopeful mornings.",
      "I want Tchaikovsky’s violin concerto to fill the room.",
      "Queue David Tao R&B classics for nostalgia.",
      "Play me some Pink Sweat$'s chill songs for a slow afternoon.",
      "Give me D.O. ballads that feel warm and honest."
    ]
  }
];

const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const createSuggestionPool = () =>
  groupedMoodSuggestions.map((group) => ({
    label: group.label,
    prompts: shuffleArray(group.prompts),
    pointer: 0
  }));

const getNextPromptFromGroup = (group) => {
  if (!group.prompts.length) {
    return '';
  }

  const prompt = group.prompts[group.pointer];
  group.pointer += 1;

  if (group.pointer >= group.prompts.length) {
    group.prompts = shuffleArray(group.prompts);
    group.pointer = 0;
  }

  return prompt;
};

const getNextSuggestionsFromPool = (pool, count = 5) => {
  if (!pool.length) {
    return [];
  }

  const selected = [];
  let groupQueue = shuffleArray(pool);

  while (selected.length < count) {
    if (!groupQueue.length) {
      groupQueue = shuffleArray(pool);
    }

    const group = groupQueue.shift();
    const prompt = getNextPromptFromGroup(group);

    if (prompt) {
      selected.push(prompt);
    } else {
      // All prompts in this group are exhausted; avoid infinite loop
      if (pool.every((g) => !g.prompts.length)) {
        break;
      }
    }
  }

  return selected;
};


export default function MoodSearch({ searchState, setSearchState }) {
  // Use persistent state from App component
  const {
    tracks,
    loading,
    error,
    showCreateButton,
    suggestedMood,
    deletedTrackIds,
    showGetMoreSongs,
    trackIdCounter,
    loadingMoreSongs,
    companionShownEarly
  } = searchState;

  // Local state for temporary message
  const [tempMessage, setTempMessage] = useState(null);

  // Helper functions to update specific parts of the state
  const updateSearchState = (updates) => {
    setSearchState(prev => ({ ...prev, ...updates }));
  };

  // Restore search state after OAuth redirect (mobile flow)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // If we already have tracks, no need to restore
    if (tracks?.validTrackInfo?.length) {
      sessionStorage.removeItem('moodtune_search_snapshot');
      return;
    }

    const snapshotRaw = sessionStorage.getItem('moodtune_search_snapshot');
    if (!snapshotRaw) {
      return;
    }

    try {
      const snapshot = JSON.parse(snapshotRaw);

      if (snapshot?.tracks?.validTrackInfo?.length) {
        updateSearchState({
          tracks: snapshot.tracks,
          showCreateButton: snapshot.showCreateButton ?? true,
          deletedTrackIds: new Set(snapshot.deletedTrackIds || []),
          showGetMoreSongs: snapshot.showGetMoreSongs ?? true,
          loadingMoreSongs: snapshot.loadingMoreSongs ?? false,
          companionShownEarly: snapshot.companionShownEarly ?? false,
          trackIdCounter: snapshot.trackIdCounter ?? snapshot.tracks.validTrackInfo.length,
          moodId: snapshot.moodId ?? null,
        });
      }
    } catch (error) {
      console.error('Error restoring search snapshot:', error);
    } finally {
      sessionStorage.removeItem('moodtune_search_snapshot');
    }
  }, []); // Run once on mount

  const suggestionPoolRef = useRef(createSuggestionPool());

  const [suggestions, setSuggestions] = useState(() =>
    getNextSuggestionsFromPool(suggestionPoolRef.current)
  );
  const shuffleSuggestions = () =>
    setSuggestions(getNextSuggestionsFromPool(suggestionPoolRef.current));

  const handleDeleteTrack = async (trackId) => {
    // Find the track to get its database ID
    const track = tracks.validTrackInfo.find(t => t._id === trackId);
    
    if (track && track.dbId) {
      // Track has database ID, delete from database
      try {
        await deleteTrack(track.dbId);
        console.log(`🗑️ Deleted track from database: ${track.name} by ${track.artist}`);
      } catch (error) {
        console.error('Error deleting track from database:', error);
        // Still remove from UI even if database deletion fails
      }
    }
    
    // Remove from UI state
    updateSearchState({
      deletedTrackIds: new Set([...deletedTrackIds, trackId])
    });
  };

  const handleMoodFetched = (newTracks) => {
    // Assign unique IDs to tracks
    const tracksWithIds = {
      ...newTracks,
      validTrackInfo: newTracks.validTrackInfo.map((track, index) => ({
        ...track,
        _id: `track_${index}` // Simple ID based on original index
      }))
    };
    
    updateSearchState({
      tracks: tracksWithIds,
      deletedTrackIds: new Set(), // Reset deleted tracks when new tracks are fetched
      showGetMoreSongs: true, // Show "Get more songs" button for new requests
      trackIdCounter: newTracks.validTrackInfo.length, // Set counter for new tracks
      companionShownEarly: false, // Reset companion shown early flag
      moodId: newTracks.moodId // Store moodId for database operations
    });
  };

  const handleCompanionShownEarly = () => {
    updateSearchState({ companionShownEarly: true });
  };

  const handleClearPreviousData = () => {
    // Clear all previous request data
    updateSearchState({
      tracks: [],
      error: null,
      deletedTrackIds: new Set(),
      showGetMoreSongs: false,
      trackIdCounter: 0,
      loadingMoreSongs: false,
      companionShownEarly: false,
      moodId: null // Reset moodId
    });
  };

  const handleGetMoreSongs = async () => {
    if (!tracks.moodDescriptions || !tracks.validTrackInfo) return;
    
    updateSearchState({ loadingMoreSongs: true, error: null });
    
    try {
      // Get current tracks (excluding deleted ones)
      const currentTracks = tracks.validTrackInfo.filter(track => !deletedTrackIds.has(track._id));
      
      const response = await getMoreSongs(
        tracks.moodDescriptions,
        currentTracks,
        searchState.moodId // Pass moodId for database saving
      );
      
      // Add new tracks to existing tracks with unique IDs
      const newTracksWithIds = response.newTracks.map((track, index) => ({
        ...track,
        _id: `track_${trackIdCounter + index}` // Use counter to ensure unique IDs
      }));
      
      const updatedTracks = {
        ...tracks,
        validTrackInfo: [...tracks.validTrackInfo, ...newTracksWithIds]
      };
      
      updateSearchState({
        tracks: updatedTracks,
        trackIdCounter: trackIdCounter + response.newTracks.length // Update counter
      });

      // Show temporary message based on number of new songs
      if (response.newTracks.length > 0) {
        setTempMessage(`Got ${response.newTracks.length} new song${response.newTracks.length > 1 ? 's' : ''} for you!`);
      } else {
        setTempMessage("There's no song left to recommend...");
      }

      // Auto-hide message after 3 seconds
      setTimeout(() => {
        setTempMessage(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error getting more songs:', error);
      updateSearchState({ error: error.message });
      // Don't hide button on error - allow user to try again
    } finally {
      updateSearchState({ loadingMoreSongs: false });
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInOut {
            0% { opacity: 0; transform: translateY(-10px); }
            20% { opacity: 1; transform: translateY(0); }
            80% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-10px); }
          }
          
          /* Responsive styles */
          @media (max-width: 768px) {
            .mood-search-page {
              padding: 1rem 0.5rem !important;
            }
            .mood-search-inner {
              padding: 1.5rem 1rem !important;
              border-radius: 15px !important;
            }
            .mood-search-title {
              font-size: 1.5rem !important;
            }
            .mood-search-subtitle {
              font-size: 1rem !important;
            }
            .mood-search-suggestion {
              font-size: 0.9rem !important;
              padding: 0.8rem 1rem !important;
            }
            .temp-message {
              font-size: 1rem !important;
              padding: 0.8rem 1.5rem !important;
              max-width: 350px !important;
            }
          }
          
          @media (max-width: 480px) {
            .mood-search-page {
              padding: 0.5rem 0.25rem !important;
            }
            .mood-search-inner {
              padding: 1rem 0.75rem !important;
              border-radius: 12px !important;
            }
            .mood-search-title {
              font-size: 1.3rem !important;
            }
            .mood-search-subtitle {
              font-size: 0.9rem !important;
            }
            .mood-search-suggestion {
              font-size: 0.85rem !important;
              padding: 0.7rem 0.8rem !important;
            }
            .temp-message {
              font-size: 0.9rem !important;
              padding: 0.7rem 1.2rem !important;
              max-width: 300px !important;
            }
          }
        `}
      </style>
      <div style={styles.page} className="mood-search-page">
        <div style={styles.inner} className="mood-search-inner">
        <h2 style={styles.title} className="mood-search-title">What's your vibe today?</h2>
        <p style={styles.subtitle} className="mood-search-subtitle">Tell me how do you feel or pick an example to get a personalized playlist.</p>

        <div style={styles.suggestionContainer}>
          {suggestions.map((sentence, index) => (
            <button
              key={index}
              onClick={() => updateSearchState({ suggestedMood: sentence })}
              style={styles.suggestionButton}
              className="mood-search-suggestion"
            >
              {sentence}
            </button>
          ))}
          <button onClick={shuffleSuggestions} style={styles.shuffleButton}>
            New Suggestions
          </button>
        </div>

        <MoodForm
          onMoodFetched={handleMoodFetched}
          setShowCreateButton={(show) => updateSearchState({ showCreateButton: show })}
          suggestedMood={suggestedMood}
          onCompanionShownEarly={handleCompanionShownEarly}
          onClearPreviousData={handleClearPreviousData}
        />

        {loading && <p style={styles.status}>Loading...</p>}
        {error && <p style={{ ...styles.status, color: 'red' }}>{error}</p>}

        {/* Temporary message for Get More Songs */}
        {tempMessage && (
          <div style={styles.tempMessage} className="temp-message">
            {tempMessage}
          </div>
        )}

        {!loading && tracks.validTrackInfo?.length > 0 && (
          <TrackList 
            tracks={tracks} 
            showCreateButton={showCreateButton}
            onDeleteTrack={handleDeleteTrack}
            deletedTrackIds={deletedTrackIds}
            showGetMoreSongs={showGetMoreSongs}
            onGetMoreSongs={handleGetMoreSongs}
            loadingMoreSongs={loadingMoreSongs}
            companionShownEarly={companionShownEarly}
          />
        )}
      </div>
    </div>
    </>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #6bbcff, #a979ff)',
    padding: '3rem 1rem',
    fontFamily: '"Segoe UI", sans-serif',
  },
  inner: {
    maxWidth: '800px',
    margin: '0 auto',
    background: 'white',
    borderRadius: '20px',
    padding: '2rem',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#333',
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  subtitle: {
    fontSize: '1.1rem',
    textAlign: 'center',
    color: '#555',
    marginBottom: '1.5rem',
  },
  suggestionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  suggestionButton: {
    fontSize: '1rem',
    padding: '0.9rem 1.25rem',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#f0f4ff',
    color: '#333',
    textAlign: 'left',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  shuffleButton: {
    fontSize: '1rem',
    padding: '0.6rem 1rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    alignSelf: 'center',
    marginTop: '1rem',
    cursor: 'pointer',
  },
  trackHeader: {
    marginTop: '2rem',
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
  },
  status: {
    textAlign: 'center',
    marginTop: '1rem',
  },
  tempMessage: {
    position: 'fixed',
    top: '75%',
    left: '0',
    right: '0',
    zIndex: 1000,
    padding: '1rem 2rem',
    backgroundColor: '#10b981',
    color: 'white',
    borderRadius: '12px',
    fontSize: '1.1rem',
    fontWeight: '600',
    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.4)',
    animation: 'fadeInOut 3s ease-in-out',
    maxWidth: '400px',
    margin: '0 auto',
    textAlign: 'center',
    backdropFilter: 'blur(10px)',
    whiteSpace: 'nowrap',
  },
};